import { Icon, LegacyCard, Text } from "@shopify/polaris"
import { BookIcon, EmailIcon } from "@shopify/polaris-icons"
import classNames from "classnames"
import copy from "copy-to-clipboard"
import { CONTACT_TYPES } from "parcelpanel-cross-sell-tracking"
import { useSmartVisibleCrossSellTracker } from "parcelpanel-polling-checker"
import { ChatIcon, ProSelectText, useMediaQuery } from "polaris-enhance"

import { EXPERTSUPPORT_OPTIONS } from "@/constant"
import { CROSS_SELL_POINT_ENUM } from "@/enums/CrossSellPoint"
import stores from "@/stores"
import { openMiddlewarePage } from "@/utils/location"
import toast from "@/utils/toast"

const SUPPORT_EMAIL = "returns@parcelpanel.ltd"

interface FeedBackHelpCardProps {
  hideEmail?: boolean
}

function FeedBackHelpCard(props: FeedBackHelpCardProps) {
  const { hideEmail } = props
  const { UserInfoStore } = stores
  const maxMedia = useMediaQuery("(max-width:1590px)")
  const isMobile = useMediaQuery("(max-width:768px)")
  const tabs = [
    {
      icon: <Icon source={EmailIcon} tone="base" />,
      title: t("GetEmailSupport"),
      content: t("ConvenientAssistanceSentDirectlyToY-0"),
      click() {
        if (isMobile) {
          copy(SUPPORT_EMAIL)
          toast("Email copied successfully.")
          return
        }
        window.open(`mailto:${SUPPORT_EMAIL}`, "_blank")
      },
    },
    {
      icon: <Icon source={ChatIcon} tone="base" />,
      title: t("LiveChatSupport"),
      content: t("InstantRealTimeHelpWithASupportAg"),
      click() {
        window.Intercom("showNewMessage", "")
      },
    },
    {
      hide: hideEmail,
      icon: <Icon source={BookIcon} tone="base" />,
      title: t("ParcelpanelReturnsHelpDocs"),
      content: t("InDepthDocumentationAndHowToGuides"),
      click() {
        window.open("https://returns-helpcenter.gitbook.io/untitled/", "_blank")
      },
    },
  ]

  const { ref } = useSmartVisibleCrossSellTracker({
    onViewed() {
      window.CrossSellTracker?.logViewed(
        CROSS_SELL_POINT_ENUM.HelpCenterContact,
      )
    },
    enabled: UserInfoStore.data.is_show_cross_sell_banner,
  })
  return (
    <LegacyCard sectioned>
      <div className="flex justify-between items-center mb-4">
        <Text variant="headingMd" as="span">
          {t("HelpCenter")}
        </Text>
        {UserInfoStore.data.is_show_cross_sell_banner ? (
          <div ref={ref}>
            <ProSelectText
              icon={
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 8C6 7.58579 6.33579 7.25 6.75 7.25H12.25C12.6642 7.25 13 7.58579 13 8C13 8.41421 12.6642 8.75 12.25 8.75H6.75C6.33579 8.75 6 8.41421 6 8Z"
                    fill="#005BD3"
                  />
                  <path
                    d="M6.75 9.5C6.33579 9.5 6 9.83579 6 10.25C6 10.6642 6.33579 11 6.75 11H10.25C10.6642 11 11 10.6642 11 10.25C11 9.83579 10.6642 9.5 10.25 9.5H6.75Z"
                    fill="#005BD3"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3 8.75C3 6.12665 5.12665 4 7.75 4H12.25C14.8734 4 17 6.12665 17 8.75V9.5C17 12.1234 14.8734 14.25 12.25 14.25H10.6241L7.44628 16.6028C7.21864 16.7713 6.91547 16.7972 6.66252 16.6698C6.40957 16.5423 6.25 16.2832 6.25 16V14.0081C4.36171 13.38 3 11.5997 3 9.5V8.75ZM7.75 5.5C5.95507 5.5 4.5 6.95507 4.5 8.75V9.5C4.5 11.0858 5.63655 12.4078 7.13978 12.6929C7.49378 12.7601 7.75 13.0695 7.75 13.4298V14.5115L9.93034 12.8972C10.0595 12.8016 10.2159 12.75 10.3766 12.75H12.25C14.0449 12.75 15.5 11.2949 15.5 9.5V8.75C15.5 6.95507 14.0449 5.5 12.25 5.5H7.75Z"
                    fill="#005BD3"
                  />
                </svg>
              }
              defaultText={t("GetExpertSupport")}
              options={EXPERTSUPPORT_OPTIONS()}
              onChange={(value) => {
                const map = {
                  demo: CONTACT_TYPES.DEMO,
                  slack: CONTACT_TYPES.SLACK,
                  whatsapp: CONTACT_TYPES.WHATS_APP,
                  wecom: CONTACT_TYPES.WE_COM,
                }
                openMiddlewarePage(
                  CROSS_SELL_POINT_ENUM.HelpCenterContact,
                  map[value],
                )
              }}
            />
          </div>
        ) : null}
      </div>
      <div
        className={classNames("grid grid-cols-1 gap-x-4", {
          "md:grid-cols-2": hideEmail,
          "md:grid-cols-3": !hideEmail,
          "gap-y-4": maxMedia,
        })}
      >
        {tabs.map((item) => {
          return (
            <div
              key={item.title}
              className="p-3 cursor-pointer hover:bg-[#F1F1F1] rounded-lg overflow-hidden"
              onClick={item.click}
              style={{
                boxShadow:
                  "1px 0px 0px 0px rgba(0, 0, 0, 0.13) inset, -1px 0px 0px 0px rgba(0, 0, 0, 0.13) inset, 0px -1px 0px 0px rgba(0, 0, 0, 0.17) inset, 0px 1px 0px 0px rgba(204, 204, 204, 0.50) inset",
              }}
            >
              <div className="flex gap-1">
                <div>{item.icon} </div>
                <div className="space-y-1">
                  <Text variant="headingSm" as="p">
                    {item.title}
                  </Text>
                  <Text variant="bodyMd" as="p">
                    {item.content}
                  </Text>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </LegacyCard>
  )
}
export default FeedBackHelpCard
